import React, { useMemo, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getStoredToken } from '../../utils/tokenStorage';

/**
 * Layer 4: Dynamic Watermark
 *
 * Renders a semi-transparent, non-selectable overlay with the user's email
 * and access timestamp. Shown for guest and trial_member users to discourage
 * screenshots and unauthorized redistribution of shared pharmaceutical data.
 *
 * Respects tenant's watermarking_enabled policy setting.
 *
 * The watermark is:
 * - CSS-only (no canvas, no images)
 * - Pointer-events: none (doesn't interfere with clicking)
 * - User-select: none (can't be selected/copied)
 * - Rotated at -30deg for full coverage
 * - Print-visible via @media print
 */
const DynamicWatermark: React.FC = () => {
  const { user } = useAuth();
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);

  // Check tenant policy for watermarking
  useEffect(() => {
    const token = getStoredToken();
    if (!token) return;
    fetch('/api/v1/sharing-policy', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data && data.watermarking_enabled === false) {
          setWatermarkEnabled(false);
        }
      })
      .catch(() => {});
  }, []);

  const shouldShow = watermarkEnabled && (user?.user_type === 'guest' || user?.user_type === 'trial_member');

  const watermarkText = useMemo(() => {
    if (!user) return '';
    const email = user.email || 'unknown';
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 16).replace('T', ' ');
    return `${email}  |  ${dateStr}`;
  }, [user]);

  if (!shouldShow) return null;

  return (
    <div
      className="dynamic-watermark"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        userSelect: 'none',
        overflow: 'hidden',
        // @ts-ignore -- webkit prefix
        WebkitUserSelect: 'none',
      }}
      aria-hidden="true"
    >
      <div
        style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          right: '-50%',
          bottom: '-50%',
          transform: 'rotate(-30deg)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '120px',
        }}
      >
        {Array.from({ length: 12 }).map((_, rowIdx) => (
          <div
            key={rowIdx}
            style={{
              display: 'flex',
              gap: '80px',
              whiteSpace: 'nowrap',
            }}
          >
            {Array.from({ length: 6 }).map((_, colIdx) => (
              <span
                key={colIdx}
                style={{
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 0.06)',
                  letterSpacing: '0.5px',
                }}
              >
                {watermarkText}
              </span>
            ))}
          </div>
        ))}
      </div>

      {/* Print-visible watermark (darker) */}
      <style>{`
        @media print {
          .dynamic-watermark span {
            color: rgba(0, 0, 0, 0.15) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DynamicWatermark;
